import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { AiChatPromptContent, AiModelIntelligenceItem, AiModelItem } from '@app/core/services/ai-chat.types';
import { TranslateService } from '@app/core/services/translate.service';
import { sanitizeAiChatPromptHtml, trimAiChatPromptTrailingParagraphs } from '@app/core/services/ai-chat.utils';
import { SirvService } from '@app/services/sirv.service';
import { EditorComponent as KendoEditorComponent, EditorPasteEvent } from '@progress/kendo-angular-editor';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { TreeItem } from '@progress/kendo-angular-treeview';
import { paperPlaneIcon } from '@progress/kendo-svg-icons';

interface AiModelTreeProviderNode {
    kind: 'provider';
    id: string;
    text: string;
    providerKeyno: string;
    children: AiModelTreeModelNode[];
}

interface AiModelTreeModelNode {
    kind: 'model';
    id: string;
    text: string;
    model: AiModelItem;
}

type AiModelTreeNode = AiModelTreeProviderNode | AiModelTreeModelNode;

interface AiChatMenuPosition {
    bottom: number;
    left: number;
    maxHeight: number;
    width: number;
}

@Component({
    selector: 'tt-ai-chat-composer',
    templateUrl: './ai-chat-composer.component.html',
    styleUrls: ['./ai-chat-composer.component.css'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false,
})
export class AiChatComposerComponent implements AfterViewInit, OnDestroy {
    public readonly sendIcon: SVGIcon = paperPlaneIcon;

    public promptPlaceholder = 'Ask AI...';

    @ViewChild('composerForm')
    private composerForm?: ElementRef<HTMLFormElement>;

    @ViewChild('editor')
    private editor?: KendoEditorComponent;

    private themeObserver?: MutationObserver;

    private promptKeydownTarget?: HTMLElement;

    private themeRequest = 0;

    private readonly onPromptKeydownCapture = (event: KeyboardEvent): void => this.onPromptKeydown(event);

    public modelMenuOpen = false;

    public modelMenuLeft = 0;

    public modelMenuBottom = 0;

    public modelMenuWidth = 0;

    public modelMenuMaxHeight = 320;

    public intelligenceMenuOpen = false;

    public intelligenceMenuLeft = 0;

    public intelligenceMenuBottom = 0;

    public intelligenceMenuWidth = 0;

    public intelligenceMenuMaxHeight = 320;

    public uploadingImages = false;

    public modelTree: AiModelTreeProviderNode[] = [];

    public expandedProviderKeys: string[] = [];

    private _models: AiModelItem[] = [];

    @Input()
    public set models(models: AiModelItem[]) {
        this._models = models ?? [];
        this.modelTree = this.buildModelTree(this._models);

        const providerKeys = new Set(this.modelTree.map((provider) => provider.id));
        this.expandedProviderKeys = this.expandedProviderKeys.filter((key) => providerKeys.has(key));
    }
    public get models() {
        return this._models;
    }

    private _intelligenceLevels: AiModelIntelligenceItem[] = [];

    @Input()
    public set intelligenceLevels(levels: AiModelIntelligenceItem[]) {
        this._intelligenceLevels = levels ?? [];
        if (this._intelligenceLevels.length <= 1) this.intelligenceMenuOpen = false;
    }
    public get intelligenceLevels() {
        return this._intelligenceLevels;
    }

    @Input()
    public selectedModelKeyno = '';

    @Input()
    public selectedIntelligenceLevel = '';

    @Input()
    public loadingIntelligenceLevels = false;

    @Input()
    public prompt = '';

    @Input()
    public loading = false;

    @Input()
    public sending = false;

    @Output()
    public selectedModelKeynoChange = new EventEmitter<string>();

    @Output()
    public selectedIntelligenceLevelChange = new EventEmitter<string>();

    @Output()
    public promptChange = new EventEmitter<string>();

    @Output()
    /** Emits provider-ready plain text and image URLs while the parent retains the editor HTML. */
    public submitPrompt = new EventEmitter<AiChatPromptContent>();

    @Output()
    public imageUploadError = new EventEmitter<string>();

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private sirv: SirvService,
        private translate: TranslateService,
    ) {}

    public setPrompt(value: string): void {
        this.prompt = sanitizeAiChatPromptHtml(value);
        this.promptChange.emit(this.prompt);
    }

    public async onPaste(event: EditorPasteEvent): Promise<void> {
        event.cleanedHtml = sanitizeAiChatPromptHtml(event.cleanedHtml);
        const pastedBase64Images = this.getBase64ImageStringsFromHtml(event.cleanedHtml);
        if (pastedBase64Images.length === 0) return;

        this.uploadingImages = true;

        try {
            if ((await this.sirv.checkConnection()) !== true) throw new Error('Sirv is not configured.');

            const uploadedImages = await Promise.all(
                pastedBase64Images.map(async (base64Image) => {
                    const file = await this.base64ImageStringToFile(base64Image);
                    if (!file) throw new Error('The pasted image could not be read.');

                    const response = await this.sirv.uploadImage(file, 'html');
                    return { base64Image, imageUrl: `${response.imageUrl}?w=800&scale.option=noup` };
                }),
            );

            const prompt = uploadedImages.reduce((html, image) => html.replaceAll(image.base64Image, image.imageUrl), this.prompt);
            this.setPrompt(prompt);
        } catch (error) {
            this.imageUploadError.emit(error instanceof Error ? error.message : 'The pasted image could not be uploaded.');
        } finally {
            this.uploadingImages = false;
        }
    }

    public selectModel(model: AiModelItem): void {
        this.selectedModelKeyno = model.ai_model_keyno;
        this.selectedModelKeynoChange.emit(this.selectedModelKeyno);
        this.modelMenuOpen = false;
    }

    public selectIntelligenceLevel(level: AiModelIntelligenceItem): void {
        this.selectedIntelligenceLevel = level.intelligence_level_id;
        this.selectedIntelligenceLevelChange.emit(this.selectedIntelligenceLevel);
        this.intelligenceMenuOpen = false;
    }

    public toggleModelMenu(event: Event): void {
        if (this.loading || this.sending || this.models.length === 0) return;

        if (!this.modelMenuOpen) {
            this.expandSelectedModelProvider();
            const position = this.getMenuPosition(event, 280);
            this.modelMenuBottom = position.bottom;
            this.modelMenuLeft = position.left;
            this.modelMenuMaxHeight = position.maxHeight;
            this.modelMenuWidth = position.width;
            this.intelligenceMenuOpen = false;
        }

        this.modelMenuOpen = !this.modelMenuOpen;
    }

    public toggleIntelligenceMenu(event: Event): void {
        if (this.loading || this.loadingIntelligenceLevels || this.sending || this.intelligenceLevels.length <= 1) return;

        if (!this.intelligenceMenuOpen) {
            const position = this.getMenuPosition(event, 180);
            this.intelligenceMenuBottom = position.bottom;
            this.intelligenceMenuLeft = position.left;
            this.intelligenceMenuMaxHeight = position.maxHeight;
            this.intelligenceMenuWidth = position.width;
            this.modelMenuOpen = false;
        }

        this.intelligenceMenuOpen = !this.intelligenceMenuOpen;
    }

    public selectTreeModel({ dataItem }: TreeItem): void {
        const node = dataItem as AiModelTreeNode;
        if (node.kind === 'model') this.selectModel(node.model);
    }

    public readonly isProviderNode = (item: object) => (item as AiModelTreeNode).kind === 'provider';

    public get selectedTreeKeys() {
        const selectedModel = this.models.find((model) => model.ai_model_keyno === this.selectedModelKeyno);
        return selectedModel ? [this.getModelNodeId(selectedModel)] : [];
    }

    public onPromptKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && (this.modelMenuOpen || this.intelligenceMenuOpen)) {
            this.consumeKeyboardEvent(event);
            this.closeMenus();
            return;
        }

        const target = event.target as HTMLElement | null;

        if (event.key !== 'Enter' || !target?.closest('.ProseMirror')) return;

        if (event.shiftKey || event.altKey || this.isPromptSelectionInList()) return;

        this.consumeKeyboardEvent(event);
        this.send();
    }

    /** Prevents a browser form submission from bypassing editor key handling. */
    public onFormSubmit(event: SubmitEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    public send(): void {
        const editorContent = this.elementRef.nativeElement.shadowRoot?.querySelector<HTMLElement>('.k-editor-content .ProseMirror');
        const sanitizedPrompt = trimAiChatPromptTrailingParagraphs(editorContent?.innerHTML || this.prompt);

        if (sanitizedPrompt !== this.prompt) {
            this.setPrompt(sanitizedPrompt);
        }

        if (this.canSend) this.submitPrompt.emit(this.getPromptContent());
    }

    public get canSend() {
        const textContent = this.getPromptTextContentFromHtml().trim();
        const containsEmbeddedContent = /<(img|video|iframe|table)\b/i.test(this.prompt);
        const containsBase64Image = /<img\b[^>]*\bsrc\s*=\s*["']data:image\//i.test(this.prompt);
        return (!!textContent || containsEmbeddedContent) && !containsBase64Image && !!this.selectedModelKeyno && !this.loading && !this.loadingIntelligenceLevels && !this.uploadingImages && !this.sending;
    }

    private consumeKeyboardEvent(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
    }

    /** Returns whether the editor selection currently belongs to a list item. */
    private isPromptSelectionInList(): boolean {
        const selection = this.editor?.view?.state.selection;

        if (selection) {
            for (let depth = selection.$from.depth; depth > 0; depth--) {
                const nodeName = selection.$from.node(depth).type.name;
                if (nodeName === 'bullet_list' || nodeName === 'ordered_list') return true;
            }

            return false;
        }

        const root = this.elementRef.nativeElement.shadowRoot as (ShadowRoot & { getSelection?: () => Selection | null }) | null;
        const browserSelection = root?.getSelection?.() ?? document.getSelection();
        const anchorNode = browserSelection?.anchorNode;
        const anchorElement = anchorNode instanceof Element ? anchorNode : anchorNode?.parentElement;

        return anchorElement?.closest('li') !== null;
    }

    private getPromptContent(): AiChatPromptContent {
        const editorContent = this.elementRef.nativeElement.shadowRoot?.querySelector<HTMLElement>('.k-editor-content .ProseMirror');
        return this.getPromptContentFromElement(editorContent?.innerHTML ? editorContent : this.getPromptHtmlContainer());
    }

    private getPromptContentFromElement(element: HTMLElement): AiChatPromptContent {
        return {
            textcontent: element.textContent ?? '',
            imageUrls: Array.from(
                new Set(
                    Array.from(element.querySelectorAll<HTMLImageElement>('img[src]'))
                        .map((image) => image.src)
                        .filter((url) => /^https?:\/\//i.test(url)),
                ),
            ),
        };
    }

    private getPromptTextContentFromHtml() {
        return this.getPromptContentFromElement(this.getPromptHtmlContainer()).textcontent;
    }

    private getPromptHtmlContainer() {
        const container = document.createElement('div');
        container.innerHTML = sanitizeAiChatPromptHtml(this.prompt);
        return container;
    }

    public get selectedModelName() {
        return this.models.find((model) => model.ai_model_keyno === this.selectedModelKeyno)?.ai_provider_model_name ?? 'No model';
    }

    public get selectedIntelligenceLevelName() {
        return this.intelligenceLevels.find((level) => level.intelligence_level_id === this.selectedIntelligenceLevel)?.intelligence_level_name ?? 'Intelligence level';
    }

    @HostListener('document:pointerdown', ['$event'])
    public closeModelMenuOnOutsidePointer(event: PointerEvent): void {
        if (this.isMenuInteraction(event)) return;

        this.closeMenus();
    }

    @HostListener('window:resize')
    public closeModelMenuOnResize(): void {
        this.closeMenus();
    }

    @HostListener('keydown', ['$event'])
    public closeModelMenuOnEscape(event: KeyboardEvent): void {
        if (event.key === 'Escape' && (this.modelMenuOpen || this.intelligenceMenuOpen)) {
            event.preventDefault();
            this.closeMenus();
        }
    }

    private closeMenus(): void {
        this.modelMenuOpen = false;
        this.intelligenceMenuOpen = false;
    }

    private getMenuPosition(event: Event, minimumWidth: number): AiChatMenuPosition {
        const target = event.target as HTMLElement | null;
        const button = target?.closest('button') ?? (event.currentTarget as HTMLElement | null);
        const composerRect = (this.composerForm?.nativeElement ?? this.elementRef.nativeElement).getBoundingClientRect();
        const buttonRect = button?.getBoundingClientRect();

        if (!buttonRect) return { bottom: 0, left: 0, maxHeight: 320, width: minimumWidth };

        const width = Math.min(Math.max(buttonRect.width, minimumWidth), composerRect.width - 24);
        return {
            bottom: composerRect.bottom - buttonRect.top + 4,
            left: Math.min(Math.max(buttonRect.left - composerRect.left, 12), composerRect.width - width - 12),
            maxHeight: Math.min(320, Math.max(80, buttonRect.top - 12)),
            width,
        };
    }

    private isMenuInteraction(event: PointerEvent) {
        const menuSelector = ['.tt-ai-chat-composer__model-menu', '.tt-ai-chat-composer__intelligence-menu', '.tt-ai-chat-composer__model-trigger', '.tt-ai-chat-composer__intelligence-trigger', '.tt-ai-chat-composer__model-button', '.tt-ai-chat-composer__intelligence-button'].join(', ');

        return event.composedPath().some((target) => target instanceof Element && target.matches(menuSelector));
    }

    private buildModelTree(models: AiModelItem[]) {
        const providers = new Map<string, AiModelTreeProviderNode>();
        const seenModels = new Set<string>();
        const sortedModels = [...models].sort((left, right) => this.getOrder(left) - this.getOrder(right));

        sortedModels.forEach((model) => {
            const modelIdentity = `${model.ai_provider_keyno}:${model.ai_model_keyno}`;
            if (seenModels.has(modelIdentity)) return;
            seenModels.add(modelIdentity);

            const providerId = this.getProviderNodeId(model.ai_provider_keyno);
            let provider = providers.get(providerId);

            if (!provider) {
                provider = {
                    kind: 'provider',
                    id: providerId,
                    text: model.ai_provider_name,
                    providerKeyno: model.ai_provider_keyno,
                    children: [],
                };
                providers.set(providerId, provider);
            }

            provider.children.push({
                kind: 'model',
                id: this.getModelNodeId(model),
                text: model.ai_model_name,
                model,
            });
        });

        return Array.from(providers.values());
    }

    private expandSelectedModelProvider() {
        const selectedModel = this.models.find((model) => model.ai_model_keyno === this.selectedModelKeyno);
        if (!selectedModel) return;

        const providerId = this.getProviderNodeId(selectedModel.ai_provider_keyno);
        if (!this.expandedProviderKeys.includes(providerId)) this.expandedProviderKeys = [...this.expandedProviderKeys, providerId];
    }

    private getProviderNodeId(providerKeyno: string) {
        return `provider:${providerKeyno}`;
    }

    private getModelNodeId(model: AiModelItem) {
        return `model:${model.ai_provider_keyno}:${model.ai_model_keyno}`;
    }

    private getOrder(model: AiModelItem) {
        const order = Number(model.orderby);
        return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
    }

    private getBase64ImageStringsFromHtml(html: string) {
        return Array.from(new Set(html.split(/["']/).filter((value) => value.startsWith('data:image/') && value.includes(';base64,'))));
    }

    private async base64ImageStringToFile(base64Image: string): Promise<File | null> {
        try {
            const response = await fetch(base64Image);
            if (!response.ok) return null;

            const blob = await response.blob();
            const extension = blob.type.split('/')[1] || 'image';
            return new File([blob], `clipboard.${extension}`, { type: blob.type });
        } catch {
            return null;
        }
    }

    ngAfterViewInit(): void {
        void this.loadPromptPlaceholder();

        this.promptKeydownTarget = this.editor?.view?.dom;
        this.promptKeydownTarget?.addEventListener('keydown', this.onPromptKeydownCapture, true);

        const sourceTheme = document.getElementById('kendo-material') as HTMLLinkElement | null;
        const shadowRoot = this.elementRef.nativeElement.shadowRoot;

        if (!sourceTheme || !shadowRoot) return;

        const scopedTheme = document.createElement('link');
        const scopedThemeVariables = document.createElement('style');
        scopedTheme.rel = 'stylesheet';
        scopedTheme.dataset['ttAiChatKendoTheme'] = '';
        scopedThemeVariables.dataset['ttAiChatKendoVariables'] = '';
        shadowRoot.prepend(scopedThemeVariables, scopedTheme);

        const updateTheme = () => void this.applyScopedTheme(sourceTheme.href, scopedTheme, scopedThemeVariables);
        updateTheme();

        this.themeObserver = new MutationObserver(updateTheme);
        this.themeObserver.observe(sourceTheme, { attributes: true, attributeFilter: ['href'] });
    }

    private async loadPromptPlaceholder(): Promise<void> {
        this.promptPlaceholder = await this.translate.translate('ask_ai');
    }

    private async applyScopedTheme(href: string, scopedTheme: HTMLLinkElement, scopedThemeVariables: HTMLStyleElement) {
        const request = ++this.themeRequest;
        scopedTheme.href = href;

        try {
            const response = await fetch(href);
            if (!response.ok) return;

            const css = await response.text();
            if (request !== this.themeRequest) return;

            const properties = Array.from(css.matchAll(/:root\s*\{([^{}]*)\}/g)).flatMap((match) => match[1].match(/--[\w-]+\s*:[^;{}]+;/g) ?? []);
            scopedThemeVariables.textContent = properties.length ? `:host { ${properties.join('\n')} }` : '';
        } catch {
            scopedThemeVariables.textContent = '';
        }
    }

    ngOnDestroy(): void {
        this.themeRequest++;
        this.themeObserver?.disconnect();
        this.promptKeydownTarget?.removeEventListener('keydown', this.onPromptKeydownCapture, true);
    }
}
